import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Share2, History, Download, Lock, Unlock, AlertTriangle } from "lucide-react";
import { DocumentsProvider, useDocuments } from "./modal/DocumentsContext";
import RichTextEditor, { DOCUMENT_MODULES, DOCUMENT_FORMATS } from "shared/RichTextEditor";
import ShareDocumentModal from "./ui/ShareDocumentModal";
import VersionHistoryModal from "./ui/VersionHistoryModal";

function formatTime(d: Date | null): string {
  if (!d) return "";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" });
}

function DocumentDetailContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    isAdmin, canEdit, currentDocument, contentEpoch, detailLoading, detailError, loadDocumentDetail,
    isDirty, markDirty, saveStatus, saveError, lastSavedAt, saveNow, contentConflict, resolveContentConflict,
    toggleReadonly, triggerExportDocx,
  } = useDocuments();

  const basePath = isAdmin ? "/admin/documents" : "/employee/documents";
  const [showShare, setShowShare] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [readonlyBusy, setReadonlyBusy] = useState(false);
  const [readonlyError, setReadonlyError] = useState<string | null>(null);

  useEffect(() => {
    if (id) loadDocumentDetail(Number(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function handleBack() {
    // Best-effort guard, same known gap as Sheets: this covers the in-app
    // Back button only — the sidebar/other <Link>s and browser back/forward
    // bypass it (see DocumentsContext's beforeunload effect for the same
    // caveat on tab close).
    if (isDirty && !window.confirm("You have unsaved changes. Leave without saving?")) return;
    navigate(basePath);
  }

  async function handleSave() {
    await saveNow();
  }

  async function handleToggleReadonly() {
    if (!currentDocument) return;
    setReadonlyBusy(true);
    setReadonlyError(null);
    try {
      await toggleReadonly(!currentDocument.is_readonly);
    } catch (err: any) {
      setReadonlyError(err?.message || err?.response?.data?.detail || "Failed to update read-only mode");
    } finally {
      setReadonlyBusy(false);
    }
  }

  if (detailLoading && !currentDocument) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (detailError || !currentDocument) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-destructive font-inter mb-3">{detailError || "Document not found"}</p>
        <button onClick={() => navigate(basePath)} className="text-sm text-primary hover:underline font-inter">
          Back to Documents
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 h-full min-h-0">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <button onClick={handleBack} className="mt-0.5 text-muted-foreground hover:text-foreground transition-colors shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-foreground font-inter truncate">{currentDocument.title}</h1>
            {currentDocument.description && (
              <p className="text-xs text-muted-foreground font-inter mt-0.5 line-clamp-1">{currentDocument.description}</p>
            )}
            <div className="flex items-center gap-2 mt-1 text-[11px] font-inter">
              {currentDocument.is_readonly && (
                <span className="flex items-center gap-1 text-amber-400">
                  <Lock className="w-3 h-3" /> Read-only
                </span>
              )}
              {canEdit && (
                <span className="text-muted-foreground">
                  {saveStatus === "saving" && "Saving…"}
                  {saveStatus === "saved" && lastSavedAt && `Last saved at ${formatTime(lastSavedAt)}`}
                  {saveStatus === "error" && <span className="text-destructive">{saveError || "Failed to save"}</span>}
                  {saveStatus === "idle" && isDirty && "Unsaved changes"}
                  {saveStatus === "idle" && !isDirty && lastSavedAt && `Last saved at ${formatTime(lastSavedAt)}`}
                </span>
              )}
              {!canEdit && currentDocument.my_permission && (
                <span className="text-muted-foreground capitalize">{currentDocument.my_permission} access</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {canEdit && (
            <button
              onClick={handleSave}
              disabled={!isDirty || saveStatus === "saving"}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium rounded-xl font-inter transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save
            </button>
          )}
          <button
            onClick={triggerExportDocx}
            title="Export as .docx"
            className="flex items-center gap-2 px-3 py-2 border border-border hover:border-muted-foreground text-muted-foreground hover:text-foreground text-sm font-medium rounded-xl font-inter transition-colors"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          {isAdmin && (
            <>
              <button
                onClick={() => setShowHistory(true)}
                title="Version history"
                className="flex items-center gap-2 px-3 py-2 border border-border hover:border-muted-foreground text-muted-foreground hover:text-foreground text-sm font-medium rounded-xl font-inter transition-colors"
              >
                <History className="w-4 h-4" />
              </button>
              <button
                onClick={() => setShowShare(true)}
                title="Share"
                className="flex items-center gap-2 px-3 py-2 border border-border hover:border-muted-foreground text-muted-foreground hover:text-foreground text-sm font-medium rounded-xl font-inter transition-colors"
              >
                <Share2 className="w-4 h-4" />
              </button>
              <button
                onClick={handleToggleReadonly}
                disabled={readonlyBusy}
                title={currentDocument.is_readonly ? "Unlock editing" : "Lock as read-only"}
                className="flex items-center gap-2 px-3 py-2 border border-border hover:border-muted-foreground text-muted-foreground hover:text-foreground text-sm font-medium rounded-xl font-inter transition-colors disabled:opacity-50"
              >
                {currentDocument.is_readonly ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
              </button>
            </>
          )}
        </div>
      </div>

      {readonlyError && (
        <p className="text-xs text-destructive font-inter bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
          {readonlyError}
        </p>
      )}

      {contentConflict && (
        <div className="flex items-center justify-between gap-3 text-xs font-inter bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
          <div className="flex items-center gap-2 text-amber-400">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            <span>This document was changed elsewhere since you opened it. Your unsaved edits can't be saved over that newer version.</span>
          </div>
          <button
            onClick={resolveContentConflict}
            className="shrink-0 px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 rounded-lg font-medium transition-colors"
          >
            Discard mine & reload latest
          </button>
        </div>
      )}

      <div className="flex-1 min-h-0">
        <RichTextEditor
          key={`${currentDocument.id}-${contentEpoch}`}
          defaultValue={currentDocument.content}
          onChange={markDirty}
          modules={DOCUMENT_MODULES}
          formats={DOCUMENT_FORMATS}
          readOnly={!canEdit}
          className="rich-text-editor--tall"
        />
      </div>

      {showShare && <ShareDocumentModal onClose={() => setShowShare(false)} />}
      {showHistory && <VersionHistoryModal onClose={() => setShowHistory(false)} />}
    </div>
  );
}

export default function DocumentDetailPage() {
  return (
    <DocumentsProvider>
      <DocumentDetailContent />
    </DocumentsProvider>
  );
}
